"use client"

import { Sheet } from "@/components/ui/sheet"
import { Surface } from "@/components/ui/surface"
import { clampFraction, clampPhase, phaseState, type OnboardingPhase } from "@/features/checkout/redesign/phases"
import { tv } from "@/lib/utils/tv"

const circle = tv({
  base: "relative flex size-10 shrink-0 items-center justify-center rounded-full",
  variants: { reached: { true: "bg-surface-brand text-fg-inverted", false: "bg-surface-page text-fg-brand" } },
})

// Each row draws the segment from its circle's centre to the next circle's centre; the row's bottom padding is the gap between rows.
const SEGMENT = "bg-line-faint absolute top-500 -bottom-500 left-500 w-1 -translate-x-1/2 overflow-hidden rounded-full"

// How far the segment leaving phase `index` is filled, in percent: the header bar's position read one gap at a time.
const segmentProgress = (index: number, active: number, fraction: number) => Math.round(Math.min(Math.max(active + clampFraction(fraction) - index, 0), 1) * 100)

// The circles cover a radius at each end, so the fill measures progress across the gap left between them.
const fillHeight = (progress: number) => `calc(var(--space-500) + (100% - 2 * var(--space-500)) * ${progress / 100})`

export interface PhasesSheetProps {
  open: boolean
  title: string
  phases: readonly OnboardingPhase[]
  current: number
  fraction?: number
  onClose: () => void
}

export const PhasesSheet = ({ open, title, phases, current, fraction = 0, onClose }: PhasesSheetProps) => {
  const active = clampPhase(current, phases.length)
  return (
    <Sheet open={open} onClose={() => onClose()} picker handle title={title}>
      <Surface variant="warm" className="rounded-10x">
        <ol className="flex flex-col">
          {phases.map((phase, index) => {
            const state = phaseState(index, active)
            const progress = segmentProgress(index, active, fraction)
            return (
              <li key={phase.id} data-state={state} aria-current={state === "current" ? "step" : undefined} className="relative flex gap-200 pb-600 last:pb-0">
                {index < phases.length - 1 && (
                  <span aria-hidden data-testid="phase-connector" data-progress={progress} className={SEGMENT}>
                    <span className="bg-brand block rounded-full" style={{ height: fillHeight(progress) }} />
                  </span>
                )}
                <span aria-hidden data-testid="phase-icon" className={circle({ reached: state === "done" || state === "current" })}>
                  {phase.icon}
                </span>
                <div className="text-body-lg flex flex-col">
                  <p className="text-fg-brand tracking-snug font-semibold">{phase.title}</p>
                  <p className="text-fg-heading">{phase.description}</p>
                </div>
              </li>
            )
          })}
        </ol>
      </Surface>
    </Sheet>
  )
}
